import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { buildPath } from "../lib/i18n";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Mail, MapPin, Phone, ArrowLeft, Loader2, Send, Clock, ShieldCheck, Scale } from "lucide-react";
import { COMPANY, RESPONSE_TIME_COPY } from "../config/company";

const COPY = {
  de: { title: "Kontakt", subtitle: "Wir sind für dich da.", copy: "Hast du Fragen zur Plattform, ein technisches Problem oder ein Anliegen zum Datenschutz? Schreib uns — wir melden uns innerhalb von 24 Stunden.", name: "Name", email: "E-Mail", subject: "Betreff", message: "Nachricht", submit: "Nachricht senden", sending: "Wird gesendet …", success: "Vielen Dank — wir haben deine Nachricht erhalten.", error: "Senden fehlgeschlagen. Bitte versuche es später erneut.", direct: "Direktkontakt", back: "Zurück" },
  fr: { title: "Contact", subtitle: "Nous sommes là pour toi.", copy: "Une question, un problème technique ou une demande de confidentialité ? Écris-nous — nous répondons sous 24 heures.", name: "Nom", email: "E-mail", subject: "Sujet", message: "Message", submit: "Envoyer le message", sending: "Envoi en cours…", success: "Merci — nous avons bien reçu ton message.", error: "Échec de l'envoi. Réessaie plus tard.", direct: "Contact direct", back: "Retour" },
  it: { title: "Contatto", subtitle: "Siamo qui per te.", copy: "Domande, problemi tecnici o richieste sulla privacy? Scrivici — rispondiamo entro 24 ore.", name: "Nome", email: "E-mail", subject: "Oggetto", message: "Messaggio", submit: "Invia messaggio", sending: "Invio in corso…", success: "Grazie — abbiamo ricevuto il tuo messaggio.", error: "Invio fallito. Riprova più tardi.", direct: "Contatto diretto", back: "Indietro" },
  en: { title: "Contact", subtitle: "We're here for you.", copy: "Have a question, a technical issue, or a privacy request? Write to us — we reply within 24 hours.", name: "Name", email: "Email", subject: "Subject", message: "Message", submit: "Send message", sending: "Sending…", success: "Thanks — we've received your message.", error: "Send failed. Please try again later.", direct: "Direct contact", back: "Back" },
};

export default function Contact() {
  const { lang } = useLang();
  const t = COPY[lang] || COPY.de;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = `${t.title} · CarHive`;
    return () => { document.title = "CarHive"; };
  }, [t.title]);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setBusy(true);
    try {
      await api.contact({ ...form, locale: lang });
      toast.success(t.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error(t.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8" data-testid="page-contact">
      <Link to={buildPath(lang, "/")} className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white">
        <ArrowLeft className="h-3.5 w-3.5" /> {t.back}
      </Link>
      <div className="mt-6 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-600/15 text-red-400"><Mail className="h-5 w-5" /></span>
        <div>
          <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">{t.title}</h1>
          <p className="mt-1 text-sm text-zinc-400">{t.subtitle}</p>
        </div>
      </div>
      <p className="mt-6 text-sm leading-relaxed text-zinc-400">{t.copy}</p>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_280px]">
        <form
          onSubmit={onSubmit}
          className="rounded-lg border border-zinc-800 bg-zinc-950 p-5 sm:p-6"
          data-testid="contact-form"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{t.name}</span>
              <input
                required minLength={2} maxLength={120}
                value={form.name} onChange={onChange("name")}
                data-testid="contact-name"
                className="mt-1.5 w-full rounded-md border border-zinc-800 bg-black px-3 py-2 text-sm text-white outline-none focus:border-red-500"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{t.email}</span>
              <input
                required type="email" maxLength={200}
                value={form.email} onChange={onChange("email")}
                data-testid="contact-email"
                className="mt-1.5 w-full rounded-md border border-zinc-800 bg-black px-3 py-2 text-sm text-white outline-none focus:border-red-500"
              />
            </label>
          </div>
          <label className="mt-4 block">
            <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{t.subject}</span>
            <input
              maxLength={160}
              value={form.subject} onChange={onChange("subject")}
              data-testid="contact-subject"
              className="mt-1.5 w-full rounded-md border border-zinc-800 bg-black px-3 py-2 text-sm text-white outline-none focus:border-red-500"
            />
          </label>
          <label className="mt-4 block">
            <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{t.message}</span>
            <textarea
              required minLength={5} maxLength={4000} rows={6}
              value={form.message} onChange={onChange("message")}
              data-testid="contact-message"
              className="mt-1.5 w-full resize-y rounded-md border border-zinc-800 bg-black px-3 py-2 text-sm text-white outline-none focus:border-red-500"
            />
          </label>
          <Button
            type="submit"
            disabled={busy}
            className="mt-5 w-full bg-red-600 text-white hover:bg-red-500 sm:w-auto"
            data-testid="contact-submit"
          >
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
            {busy ? t.sending : t.submit}
          </Button>
        </form>

        <aside className="rounded-2xl border border-zinc-800 bg-zinc-950 p-5 text-sm sm:p-6" data-testid="contact-direct-card">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-500">{t.direct}</p>
          <p className="mt-3 font-display text-lg font-semibold text-white">{COMPANY.operator.fullName}</p>
          <p className="text-sm text-zinc-500">{COMPANY.name}</p>
          {COMPANY.hasAddress && (
            <p className="mt-3 flex items-start gap-2 text-zinc-400">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-500" />
              <span>{COMPANY.operator.street}<br />{COMPANY.operator.zip} {COMPANY.operator.city}<br />{COMPANY.operator.country}</span>
            </p>
          )}

          <span className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] text-emerald-200">
            <Clock className="h-3 w-3" /> {RESPONSE_TIME_COPY[lang] || RESPONSE_TIME_COPY.de}
          </span>

          <div className="mt-4 space-y-2">
            <a href={`mailto:${COMPANY.emails.general}`} className="flex items-center gap-2 text-zinc-200 hover:text-white" data-testid="contact-direct-general">
              <Mail className="h-3.5 w-3.5 text-red-400" />
              <span className="break-all">{COMPANY.emails.general}</span>
            </a>
            <a href={`mailto:${COMPANY.emails.support}`} className="flex items-center gap-2 text-zinc-200 hover:text-white" data-testid="contact-direct-support">
              <Mail className="h-3.5 w-3.5 text-red-400" />
              <span className="break-all">{COMPANY.emails.support}</span>
              <span className="ml-auto text-[10px] uppercase tracking-wider text-zinc-500">Support</span>
            </a>
            <a href={`mailto:${COMPANY.emails.privacy}`} className="flex items-center gap-2 text-zinc-200 hover:text-white" data-testid="contact-direct-privacy">
              <ShieldCheck className="h-3.5 w-3.5 text-red-400" />
              <span className="break-all">{COMPANY.emails.privacy}</span>
            </a>
            <a href={`mailto:${COMPANY.emails.legal}`} className="flex items-center gap-2 text-zinc-200 hover:text-white" data-testid="contact-direct-legal">
              <Scale className="h-3.5 w-3.5 text-red-400" />
              <span className="break-all">{COMPANY.emails.legal}</span>
            </a>
            {COMPANY.hasPhone && COMPANY.phone && (
              <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-zinc-200 hover:text-white" data-testid="contact-direct-phone">
                <Phone className="h-3.5 w-3.5 text-red-400" />
                <span>{COMPANY.phone}</span>
              </a>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
